// Phase 23.E.6 — Rule Editor Drawer (Shadcn Sheet) with non-gated 5-step
// stepper. Replaces the inline-expanded form blocks in the old RuleBuilder
// row. Single rule edits open here; quick edits (priority, exclude,
// duplicate, delete, reorder) stay on the list row.
//
// Layout
//   • Desktop (md+):  Sheet from the right, max-w 760px, vertical stepper on left.
//   • Mobile (<md):   Full-screen sheet, horizontal step pill bar at top.
//   • Sticky footer with [Cancel] [Save & Close] always reachable above the keyboard.
//
// Step gating policy: NONE. Admins can jump between steps freely; each
// step shows a status pip (✓ ok / ⚠ incomplete / · empty). This keeps the
// editor friendly for surgical edits (most admin work is "tweak one
// field" not "build from scratch").
//
// State model: the drawer is CONTROLLED — every change calls `onChange`
// against the in-memory draft. `Save & Close` just closes the drawer
// because the draft is already up-to-date in the parent. The drawer
// never POSTs the rule itself; that's the page-level "Save draft" button.
import { useMemo, useState } from 'react';
import {
  Check, AlertTriangle, Tag, Settings2, Banknote, Layers, FileCheck2,
  ShieldCheck, Link2, Lock, Sparkles,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { AdvancedConditions } from '@/components/admin/AdvancedConditions';
import { CategoryMultiSelect } from '@/components/admin/CategoryMultiSelect';
import {
  ChipList, ConfidenceBadge, defaultProvenance,
  CONFIDENCE_LEVELS, SOURCE_TYPES, VERIFICATION_STATUSES,
  EARN_TYPES, PERIODS, MODES, CAP_KINDS,
  capKind, setCapKind as helperSetCapKind,
  poolDimension, poolLimit, formatPoolLimit,
} from '@/components/admin/ruleBuilderShared';


// ──────────────────────────────────────────────────────────────────────
// Step definitions
// ──────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 'match',    label: 'Matching',  icon: Tag,        short: 'Match' },
  { id: 'context',  label: 'Context',   icon: Settings2,  short: 'Ctx' },
  { id: 'reward',   label: 'Reward',    icon: Banknote,   short: 'Earn' },
  { id: 'caps',     label: 'Caps & Pools', icon: Layers,  short: 'Caps' },
  { id: 'review',   label: 'Review',    icon: FileCheck2, short: 'Done' },
];


// ──────────────────────────────────────────────────────────────────────
// Step status — used to render the ✓ / ⚠ / · pip in the stepper.
// Soft signals only; nothing gates progression.
// ──────────────────────────────────────────────────────────────────────
function stepStatus(stepId, rule) {
  if (!rule) return 'empty';
  if (stepId === 'match') {
    const has = (rule.match?.categories || []).length
      || (rule.match?.mcc || []).length
      || (rule.match?.merchants || []).length
      || (rule.match?.contexts || []).length;
    return has ? 'ok' : 'warn';
  }
  if (stepId === 'context') {
    const list = rule.match?.contexts || [];
    if (list.length === 0) return 'empty';
    const anyEmpty = list.some((c) => !(c.values || []).length);
    return anyEmpty ? 'warn' : 'ok';
  }
  if (stepId === 'reward') {
    if (rule.exclude) return 'ok';
    const e = rule.earn || {};
    const needsPer = e.type === 'rate_per_spend';
    if (!e.type) return 'warn';
    if (!(e.rate > 0)) return 'warn';
    if (needsPer && !(e.per > 0)) return 'warn';
    return 'ok';
  }
  if (stepId === 'caps') {
    if (!rule.cap) return 'empty';
    if (rule.cap.pool_id) return 'ok';
    const k = capKind(rule.cap);
    return (rule.cap[k] ?? null) != null ? 'ok' : 'warn';
  }
  if (stepId === 'review') return 'ok';
  return 'empty';
}


const STATUS_TONE = {
  ok:    { dot: 'bg-emerald-500',   ring: 'ring-emerald-500/40',  Icon: Check },
  warn:  { dot: 'bg-amber-500',     ring: 'ring-amber-500/40',    Icon: AlertTriangle },
  empty: { dot: 'bg-muted-foreground/30', ring: 'ring-border',    Icon: null },
};


// ──────────────────────────────────────────────────────────────────────
// Drawer container
// ──────────────────────────────────────────────────────────────────────
export const RuleEditorDrawer = ({
  open, onClose,
  rule, onChange,
  capPools, allRules, cardDefaults,
  locked,
}) => {
  const [activeStep, setActiveStep] = useState(() => {
    // Open new rules on Step 1, existing rules with contexts on Step 2.
    if (!rule) return 'match';
    if ((rule.match?.contexts || []).length > 0) return 'context';
    return 'match';
  });

  // Quick helpers
  const set = (patch) => onChange({ ...rule, ...patch });
  const setMatch = (patch) => set({ match: { ...(rule.match || {}), ...patch } });
  const setEarn = (patch) => set({ earn: { ...(rule.earn || {}), ...patch } });
  const setCap = (patch) => set({ cap: { ...(rule.cap || {}), ...patch } });
  const setProvenance = (patch) => set({ provenance: { ...(rule.provenance || defaultProvenance()), ...patch } });

  const provenance = rule?.provenance || defaultProvenance();
  const statusMap = useMemo(() => {
    const m = {};
    for (const s of STEPS) m[s.id] = stepStatus(s.id, rule);
    return m;
  // eslint-disable-next-line
  }, [JSON.stringify(rule || {})]);

  if (!rule) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className={cn(
          'p-0 flex flex-col gap-0',
          // Full-screen on mobile, fixed width on desktop. Tailwind's
          // arbitrary width handles the breakpoint cleanly.
          'w-full sm:max-w-[760px] sm:w-[760px]',
        )}
        data-testid={`admin-rule-editor-drawer-${rule.id}`}
      >
        {/* Header. The redundant custom close button has been removed —
            Radix's SheetContent renders its own close affordance in the
            top-right corner. We keep `pr-8` on the input row so the
            ConfidenceBadge doesn't slide under it. */}
        <header className="px-4 sm:px-5 py-3 border-b border-border bg-[hsl(var(--surface-2))]/30 shrink-0">
          <div className="flex items-center gap-2 pr-8">
            <Tag className="h-4 w-4 text-accent shrink-0" />
            <Input
              value={rule.label || ''}
              onChange={(e) => set({ label: e.target.value })}
              disabled={locked}
              placeholder="Rule label (e.g. Dining 5x on SmartBuy)"
              className="h-8 text-sm font-medium flex-1 min-w-0"
              data-testid={`admin-rule-builder-label-${rule.id}`}
            />
            <ConfidenceBadge provenance={provenance} className="hidden sm:inline-flex" />
          </div>
          {/* Mobile step pill bar */}
          <div className="mt-3 flex md:hidden gap-1.5 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
            {STEPS.map((s, i) => (
              <StepPill
                key={s.id}
                step={s}
                index={i}
                active={activeStep === s.id}
                status={statusMap[s.id]}
                onClick={() => setActiveStep(s.id)}
              />
            ))}
          </div>
        </header>

        {/* Body: vertical stepper sidebar (desktop) + main content */}
        <div className="flex-1 flex flex-row min-h-0">
          {/* Vertical stepper — desktop only */}
          <nav className="hidden md:flex flex-col w-[180px] shrink-0 border-r border-border bg-[hsl(var(--surface-2))]/20 p-3 gap-1">
            {STEPS.map((s, i) => (
              <StepNavItem
                key={s.id}
                step={s}
                index={i}
                active={activeStep === s.id}
                status={statusMap[s.id]}
                onClick={() => setActiveStep(s.id)}
              />
            ))}
            <div className="mt-auto pt-3 border-t border-border">
              <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Hint</div>
              <div className="text-[10px] text-muted-foreground leading-snug">
                Steps can be filled in any order. The page-level <b>Save draft</b> button persists everything to backend.
              </div>
            </div>
          </nav>

          {/* Main content */}
          <main className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4 min-w-0">
            {activeStep === 'match' && (
              <Step1Matching rule={rule} setMatch={setMatch} set={set} locked={locked} cardDefaults={cardDefaults} />
            )}
            {activeStep === 'context' && (
              <Step2Context rule={rule} setMatch={setMatch} locked={locked} />
            )}
            {activeStep === 'reward' && (
              <Step3Reward rule={rule} setEarn={setEarn} set={set} locked={locked} cardDefaults={cardDefaults} />
            )}
            {activeStep === 'caps' && (
              <Step4Caps rule={rule} setCap={setCap} set={set} capPools={capPools} allRules={allRules} locked={locked} />
            )}
            {activeStep === 'review' && (
              <Step5Review rule={rule} capPools={capPools} setProvenance={setProvenance} set={set} provenance={provenance} locked={locked} />
            )}
          </main>
        </div>

        {/* Sticky footer */}
        <footer className="px-4 sm:px-5 py-3 border-t border-border bg-background flex items-center justify-between gap-2 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground hidden sm:block">
            {locked ? 'Read-only · forked draft to edit' : 'Edits stay in this draft until you Save'}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-[11px]" data-testid={`admin-rule-editor-cancel-${rule.id}`}>
              Cancel
            </Button>
            <Button size="sm" onClick={onClose} className="text-[11px] gap-1.5" data-testid={`admin-rule-editor-save-${rule.id}`}>
              <Check className="h-3.5 w-3.5" /> Save &amp; Close
            </Button>
          </div>
        </footer>
      </SheetContent>
    </Sheet>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Stepper widgets
// ──────────────────────────────────────────────────────────────────────
const StepNavItem = ({ step, index, active, status, onClick }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.empty;
  const Icon = step.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left flex items-center gap-2 px-2 py-2 rounded-md transition-colors',
        active
          ? 'bg-primary/10 ring-1 ring-primary/30 text-foreground'
          : 'hover:bg-muted/40 text-muted-foreground hover:text-foreground',
      )}
      data-testid={`admin-rule-editor-step-${step.id}`}
      aria-current={active ? 'step' : undefined}
    >
      <div className={cn('h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-mono-num ring-2', tone.ring, active ? 'bg-background' : 'bg-[hsl(var(--surface-2))]')}>
        {tone.Icon ? <tone.Icon className={cn('h-3 w-3', status === 'ok' ? 'text-emerald-500' : status === 'warn' ? 'text-amber-500' : 'text-muted-foreground')} /> : (index + 1)}
      </div>
      <div className="min-w-0">
        <div className="text-[12px] font-medium truncate flex items-center gap-1.5">
          <Icon className="h-3 w-3 opacity-70" /> {step.label}
        </div>
      </div>
    </button>
  );
};

const StepPill = ({ step, index, active, status, onClick }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.empty;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] transition-colors border',
        active
          ? 'bg-primary/10 border-primary/30 text-foreground font-medium'
          : 'bg-background border-input text-muted-foreground',
      )}
      data-testid={`admin-rule-editor-step-pill-${step.id}`}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} />
      <span>{index + 1}. {step.short}</span>
    </button>
  );
};


// ──────────────────────────────────────────────────────────────────────
// STEP 1 — Matching Criteria
// ──────────────────────────────────────────────────────────────────────
const Step1Matching = ({ rule, setMatch, set, locked }) => {
  return (
    <section className="space-y-4" data-testid={`admin-rule-editor-section-match-${rule.id}`}>
      <StepHeader icon={Tag} title="Matching Criteria" subtitle="When should this rule fire? Category + Merchant + MCC are OR — any one matching triggers." />

      <div className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3 space-y-3">
        {/* Phase-25.B: Categories are now picked from the canonical registry
            (managed at /admin/categories). Multi-select with checkboxes —
            inline "+ Create new" is available when the typed text doesn't
            match any existing value or alias. The canonical value is what
            gets stored in rule.match.categories[]. */}
        <div>
          <div className="text-xs font-medium text-foreground mb-1.5">Categories</div>
          <CategoryMultiSelect
            value={rule.match?.categories || []}
            onChange={(v) => setMatch({ categories: v })}
            locked={locked}
            testid={`admin-rule-builder-cat-${rule.id}`}
          />
          <p className="text-[10px] text-muted-foreground mt-1.5">
            Pick one or more canonical categories. Manage the full list in <code className="text-[10px]">/admin/categories</code>.
          </p>
        </div>
        <ChipList
          label="MCC codes"
          items={rule.match?.mcc || []}
          onChange={(v) => setMatch({ mcc: v })}
          placeholder="5811, 5812"
          testid={`admin-rule-builder-mcc-${rule.id}`}
          locked={locked}
        />
        <ChipList
          label="Merchant slugs"
          items={rule.match?.merchants || []}
          onChange={(v) => setMatch({ merchants: v })}
          placeholder="swiggy, smartbuy, makemytrip"
          testid={`admin-rule-builder-merch-${rule.id}`}
          locked={locked}
        />
      </div>

      <div className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Channel filters</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Mode</Label>
            <Select value={rule.match?.mode || 'any'} onValueChange={(v) => setMatch({ mode: v })} disabled={locked}>
              <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-builder-mode-${rule.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">International</Label>
            <Select
              value={rule.match?.international === null || rule.match?.international === undefined ? 'any' : (rule.match.international ? 'yes' : 'no')}
              onValueChange={(v) => setMatch({ international: v === 'any' ? null : v === 'yes' })}
              disabled={locked}
            >
              <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-builder-intl-${rule.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="yes">International only</SelectItem>
                <SelectItem value="no">Domestic only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-dashed border-border p-3 flex items-center justify-between">
        <div>
          <div className="text-[11px] font-medium flex items-center gap-1.5"><Lock className="h-3 w-3 text-rose-500" /> Exclude</div>
          <div className="text-[10px] text-muted-foreground">Block reward earning even if this rule matches.</div>
        </div>
        <Switch
          checked={!!rule.exclude}
          disabled={locked}
          onCheckedChange={(v) => set({ exclude: v, earn: v ? null : (rule.earn || { type: 'rate_per_spend', currency: 'RP', rate: 1, per: 100, point_value_inr: 1 }) })}
          data-testid={`admin-rule-builder-exclude-${rule.id}`}
        />
      </div>
    </section>
  );
};


// ──────────────────────────────────────────────────────────────────────
// STEP 2 — Transaction Context (delegates to AdvancedConditions)
// ──────────────────────────────────────────────────────────────────────
const Step2Context = ({ rule, setMatch, locked }) => {
  return (
    <section className="space-y-4" data-testid={`admin-rule-editor-section-context-${rule.id}`}>
      <StepHeader
        icon={Settings2}
        title="Transaction Context"
        subtitle="Optional. Narrow this rule beyond category — e.g. only when Portal/Channel ∈ {Bank SmartBuy} and Subcategory ∈ {International flight}."
      />
      <AdvancedConditions
        ruleId={rule.id}
        conditions={rule.match?.contexts || []}
        legacyMatch={rule.match || {}}
        onChange={(next) => setMatch({ contexts: next })}
        locked={locked}
      />
    </section>
  );
};


// ──────────────────────────────────────────────────────────────────────
// STEP 3 — Reward Definition
// ──────────────────────────────────────────────────────────────────────
const Step3Reward = ({ rule, setEarn, locked }) => {
  const earnType = rule.earn?.type || 'rate_per_spend';
  if (rule.exclude) {
    return (
      <section className="space-y-4" data-testid={`admin-rule-editor-section-reward-${rule.id}`}>
        <StepHeader icon={Banknote} title="Reward Definition" subtitle="This rule is set to Exclude — no reward earned when it matches." />
        <Card className="p-4 border-rose-500/30 bg-rose-500/5 flex items-center gap-2">
          <Lock className="h-4 w-4 text-rose-500" />
          <div className="text-[12px] text-rose-700 dark:text-rose-300">
            <b>Exclude rule</b> — reward block hidden. Toggle <em>Exclude</em> off in Step 1 to set a reward.
          </div>
        </Card>
      </section>
    );
  }
  return (
    <section className="space-y-4" data-testid={`admin-rule-editor-section-reward-${rule.id}`}>
      <StepHeader icon={Banknote} title="Reward Definition" subtitle="What does this card earn when the rule fires?" />
      <div className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3 space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Earn type</Label>
          <Select value={earnType} onValueChange={(v) => setEarn({ type: v })} disabled={locked}>
            <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-builder-earn-type-${rule.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{EARN_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Currency</Label>
            <Input value={rule.earn?.currency || 'RP'} onChange={(e) => setEarn({ currency: e.target.value })} disabled={locked} className="mt-1 h-9 text-xs" data-testid={`admin-rule-builder-earn-currency-${rule.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Pt value (₹)</Label>
            <Input type="number" step="0.01" value={rule.earn?.point_value_inr ?? 1} onChange={(e) => setEarn({ point_value_inr: Number(e.target.value) || 0 })} disabled={locked} className="mt-1 h-9 font-mono-num text-xs" data-testid={`admin-rule-builder-earn-ptval-${rule.id}`} />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Rate</Label>
            <Input type="number" step="0.01" value={rule.earn?.rate ?? 0} onChange={(e) => setEarn({ rate: Number(e.target.value) || 0 })} disabled={locked} className="mt-1 h-9 font-mono-num text-xs" data-testid={`admin-rule-builder-earn-rate-${rule.id}`} />
          </div>
          {earnType === 'rate_per_spend' && (
            <div>
              <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Per (₹ spent)</Label>
              <Input type="number" value={rule.earn?.per ?? 100} onChange={(e) => setEarn({ per: Number(e.target.value) || 1 })} disabled={locked} className="mt-1 h-9 font-mono-num text-xs" data-testid={`admin-rule-builder-earn-per-${rule.id}`} />
            </div>
          )}
        </div>
        <div className="rounded border border-dashed border-border bg-background/60 px-2.5 py-2 text-[11px] text-muted-foreground">
          {earnType === 'percent_cashback' ? (
            <>Earn <b className="text-foreground">{rule.earn?.rate || 0}%</b> as cashback on eligible spend.</>
          ) : (
            <>Earn <b className="text-foreground">{rule.earn?.rate || 0}</b> {rule.earn?.currency || 'RP'} for every <b className="text-foreground">₹{rule.earn?.per || 0}</b> spent ({(rule.earn?.point_value_inr || 1).toFixed(2)} ₹/pt).</>
          )}
        </div>
      </div>
    </section>
  );
};


// ──────────────────────────────────────────────────────────────────────
// STEP 4 — Caps & Pools
// ──────────────────────────────────────────────────────────────────────
const Step4Caps = ({ rule, setCap, set, capPools, allRules, locked }) => {
  const sharedPoolId = rule.cap?.pool_id || null;
  const sharedPool = sharedPoolId ? (capPools || []).find((p) => p.id === sharedPoolId) : null;
  const capFieldsDisabled = locked || !!sharedPoolId;
  const spilloverOn = (rule.cap?.spillover_mode || 'block') === 'fallback_rule';
  const fallbackCandidates = (allRules || []).filter((r) => r && r.id && r.id !== rule.id && !r.exclude && r.earn);
  const inheritedFallbackId = sharedPool ? sharedPool.fallback_rule_id : null;
  const inheritedFallbackRule = inheritedFallbackId ? (allRules || []).find((r) => r.id === inheritedFallbackId) : null;

  return (
    <section className="space-y-4" data-testid={`admin-rule-editor-section-caps-${rule.id}`}>
      <StepHeader icon={Layers} title="Caps & Pools" subtitle="Optional. Limit how much reward this rule earns per period, or share a counter with other rules." />

      <div className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px] font-medium">
            <Layers className="h-3 w-3 text-amber-500" /> Enable cap
          </div>
          <Switch
            checked={!!rule.cap}
            disabled={locked}
            onCheckedChange={(v) => set({ cap: v ? { amount_inr: 100000, period: 'monthly', spillover_mode: 'fallback_rule' } : null })}
            data-testid={`admin-rule-builder-cap-toggle-${rule.id}`}
          />
        </div>
        {rule.cap && (
          <div className="mt-3 space-y-3">
            {((capPools && capPools.length > 0) || sharedPoolId) && (
              <div className="rounded border border-border bg-background/60 p-2.5">
                <div className="flex items-center gap-1.5 mb-1">
                  <Link2 className="h-3 w-3 text-accent" />
                  <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Shared pool (optional)</Label>
                </div>
                <Select value={sharedPoolId || '__none__'} onValueChange={(v) => setCap({ pool_id: v === '__none__' ? null : v })} disabled={locked}>
                  <SelectTrigger className="h-9 text-xs" data-testid={`admin-rule-builder-cap-pool-${rule.id}`}>
                    <SelectValue placeholder="None — rule has its own cap" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None — use rule-local cap below</SelectItem>
                    {(capPools || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label} — {formatPoolLimit(p)} / {p.period}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {sharedPool && (
                  <div className="mt-1.5 text-[10px] text-muted-foreground">
                    Pool config overrides rule-local cap. Multiple rules sharing this pool drain one counter.
                  </div>
                )}
              </div>
            )}

            <div className={cn('grid grid-cols-1 sm:grid-cols-3 gap-2', sharedPoolId && 'opacity-50')}>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cap on</Label>
                <Select value={capKind(rule.cap)} onValueChange={(v) => helperSetCapKind(rule.cap, v, setCap)} disabled={capFieldsDisabled}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid={`admin-rule-builder-cap-kind-${rule.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount_inr">Spend (₹)</SelectItem>
                    <SelectItem value="reward_inr">Reward (₹)</SelectItem>
                    <SelectItem value="reward_points">Reward (points)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Limit</Label>
                <Input
                  type="number"
                  inputMode="numeric"
                  value={rule.cap[capKind(rule.cap)] ?? ''}
                  onChange={(e) => {
                    const kind = capKind(rule.cap);
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    setCap({ [kind]: v });
                  }}
                  disabled={capFieldsDisabled}
                  className="mt-1 h-9 font-mono-num text-xs"
                  data-testid={`admin-rule-builder-cap-value-${rule.id}`}
                  placeholder={capKind(rule.cap) === 'reward_points' ? '50000' : '100000'}
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Per</Label>
                <Select value={rule.cap?.period || 'monthly'} onValueChange={(v) => setCap({ period: v })} disabled={capFieldsDisabled}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid={`admin-rule-builder-cap-period-${rule.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <label className={cn('flex items-start gap-2 cursor-pointer select-none', sharedPoolId && 'opacity-50')} data-testid={`admin-rule-builder-cap-spillover-${rule.id}`}>
              <Switch
                checked={spilloverOn}
                disabled={capFieldsDisabled}
                onCheckedChange={(v) => setCap({ spillover_mode: v ? 'fallback_rule' : 'block' })}
              />
              <span className="text-[11px] leading-tight">
                <span className="font-medium">Spillover when cap is reached</span>
                <span className="block text-[10px] text-muted-foreground mt-0.5">
                  Over-cap spend earns at the fallback rule below. Off = no reward on the excess.
                </span>
              </span>
            </label>

            {spilloverOn && !sharedPoolId && (
              <div className="pl-12" data-testid={`admin-rule-builder-cap-fallback-row-${rule.id}`}>
                <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Fallback target</Label>
                <Select value={rule.cap?.fallback_rule_id || '__auto__'} onValueChange={(v) => setCap({ fallback_rule_id: v === '__auto__' ? null : v })} disabled={locked}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid={`admin-rule-builder-cap-fallback-${rule.id}`}>
                    <SelectValue placeholder="Auto · next-priority rule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__" className="text-xs">Auto · next-priority matching rule</SelectItem>
                    {fallbackCandidates.length > 0 && (
                      <>
                        <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Or pick explicitly:</div>
                        {fallbackCandidates.map((r) => (
                          <SelectItem key={r.id} value={r.id} className="text-xs">{r.label || r.id}</SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-[10px] text-muted-foreground leading-snug">
                  Leave on <em>Auto</em> for simple cards. Pick a specific rule only when you need to override priority routing.
                </p>
              </div>
            )}
            {spilloverOn && sharedPoolId && (
              <div className="pl-12" data-testid={`admin-rule-builder-cap-fallback-inherited-${rule.id}`}>
                <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))]/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                  <span className="font-medium">Fallback inherited from pool</span>
                  {inheritedFallbackRule ? (
                    <span> · routes to <span className="font-medium text-foreground">{inheritedFallbackRule.label || inheritedFallbackRule.id}</span></span>
                  ) : (
                    <span> · pool uses auto routing</span>
                  )}
                  <span className="block text-[10px] mt-0.5">Edit the pool to change this.</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};


// ──────────────────────────────────────────────────────────────────────
// STEP 5 — Review (rule summary + provenance + notes + audit)
// ──────────────────────────────────────────────────────────────────────
const Step5Review = ({ rule, capPools, setProvenance, set, provenance, locked }) => {
  const sharedPool = rule.cap?.pool_id ? (capPools || []).find((p) => p.id === rule.cap.pool_id) : null;
  // eslint-disable-next-line
  const summary = useMemo(() => buildRuleSummary(rule, sharedPool), [JSON.stringify(rule || {}), JSON.stringify(sharedPool || {})]);

  return (
    <section className="space-y-4" data-testid={`admin-rule-editor-section-review-${rule.id}`}>
      <StepHeader icon={FileCheck2} title="Review" subtitle="Confirm what you built. Provenance, notes, and audit live here too." />

      {/* Rule summary card */}
      <Card className="p-3 border-primary/30 bg-primary/5" data-testid={`admin-rule-editor-summary-${rule.id}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              <Sparkles className="h-3 w-3 text-accent" /> Rule summary
            </div>
            <div className="font-medium text-sm truncate">{rule.label || '(unlabeled rule)'}</div>
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0">priority {rule.priority ?? 10}</Badge>
        </div>
        <ol className="text-[12px] leading-relaxed space-y-0.5 mt-1.5 pl-1">
          {summary.map((line, i) => (
            <li key={i} className="flex gap-1.5 items-baseline">
              <span className="text-muted-foreground font-mono-num shrink-0 text-[10px] mt-0.5">{line.kind}</span>
              <span className="min-w-0">{line.text}</span>
            </li>
          ))}
        </ol>
      </Card>

      {/* Notes */}
      <div className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3">
        <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Notes for operators</Label>
        <Textarea
          value={rule.notes || ''}
          onChange={(e) => set({ notes: e.target.value })}
          rows={3}
          placeholder="Conditions, observed quirks, support ticket links…"
          disabled={locked}
          className="mt-1.5 text-[11px]"
          data-testid={`admin-rule-builder-notes-${rule.id}`}
        />
      </div>

      {/* Provenance — collapsible */}
      <details className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-3" data-testid={`admin-rule-builder-provenance-${rule.id}`}>
        <summary className="flex items-center justify-between cursor-pointer list-none">
          <div className="flex items-center gap-1.5 text-[11px] font-medium">
            <ShieldCheck className="h-3 w-3 text-accent" /> Source &amp; confidence
          </div>
          <ConfidenceBadge provenance={provenance} />
        </summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Confidence</Label>
            <Select value={provenance.confidence_level || 'official'} onValueChange={(v) => setProvenance({ confidence_level: v })} disabled={locked}>
              <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-prov-confidence-${rule.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{CONFIDENCE_LEVELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Source type</Label>
            <Select value={provenance.source_type || 'issuer_docs'} onValueChange={(v) => setProvenance({ source_type: v })} disabled={locked}>
              <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-prov-source-${rule.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCE_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Verification</Label>
            <Select value={provenance.verification_status || 'verified'} onValueChange={(v) => setProvenance({ verification_status: v })} disabled={locked}>
              <SelectTrigger className="mt-1 h-9" data-testid={`admin-rule-prov-verification-${rule.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{VERIFICATION_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Source URL</Label>
            <Input value={provenance.source_url || ''} onChange={(e) => setProvenance({ source_url: e.target.value })} placeholder="https://..." disabled={locked} className="mt-1 h-9 text-xs" data-testid={`admin-rule-prov-url-${rule.id}`} />
          </div>
          <div className="flex items-end justify-between rounded border border-border bg-background px-2 py-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider">Community</Label>
              <div className="text-[10px] text-muted-foreground">Reproduced by users</div>
            </div>
            <Switch checked={!!provenance.community_verified} onCheckedChange={(v) => setProvenance({ community_verified: v })} disabled={locked} data-testid={`admin-rule-prov-community-${rule.id}`} />
          </div>
          <div className="sm:col-span-3">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Observation notes</Label>
            <Textarea value={provenance.observation_notes || ''} onChange={(e) => setProvenance({ observation_notes: e.target.value })} rows={2} placeholder="Conditions, processor quirks, portal routing requirements…" disabled={locked} className="mt-1 text-[11px]" data-testid={`admin-rule-prov-notes-${rule.id}`} />
          </div>
        </div>
      </details>
    </section>
  );
};


// Build a "Rule matches when X · Earns Y · Capped at Z" readback.
function buildRuleSummary(rule, sharedPool) {
  const lines = [];
  const cats = rule.match?.categories || [];
  const mcc = rule.match?.mcc || [];
  const merchants = rule.match?.merchants || [];
  const contexts = rule.match?.contexts || [];

  if (cats.length) lines.push({ kind: '•',   text: <>Category <b>=</b> {cats.join(' | ')}</> });
  if (mcc.length)  lines.push({ kind: 'OR',  text: <>MCC <b>∈</b> {`{${mcc.join(', ')}}`}</> });
  if (merchants.length) lines.push({ kind: 'OR', text: <>Merchant <b>∈</b> {`{${merchants.join(', ')}}`}</> });
  if (rule.match?.mode && rule.match.mode !== 'any') lines.push({ kind: 'AND', text: <>Mode <b>=</b> {rule.match.mode}</> });
  if (rule.match?.international === true)  lines.push({ kind: 'AND', text: <>International only</> });
  if (rule.match?.international === false) lines.push({ kind: 'AND', text: <>Domestic only</> });
  for (const c of contexts) {
    const op = c.operator === 'not_in' ? '∉' : '∈';
    const label = c.context_type === 'custom' ? `Custom · ${c.custom_key}` : c.context_type;
    lines.push({ kind: 'AND', text: <>{label} <b>{op}</b> {`{${(c.values || []).join(', ')}}`}</> });
  }
  if (lines.length === 0) {
    lines.push({ kind: '·', text: <span className="text-amber-500">No criteria yet — rule will never match.</span> });
  }

  // Earn
  if (rule.exclude) {
    lines.push({ kind: '⇒', text: <span className="text-rose-500"><b>EXCLUDE</b> — no reward earned</span> });
  } else if (rule.earn?.type === 'percent_cashback') {
    lines.push({ kind: '⇒', text: <>Earn <b>{rule.earn?.rate || 0}%</b> cashback</> });
  } else {
    lines.push({ kind: '⇒', text: <>Earn <b>{rule.earn?.rate || 0} {rule.earn?.currency || 'RP'}</b> per <b>₹{rule.earn?.per || 0}</b></> });
  }

  // Cap
  if (rule.cap?.pool_id && sharedPool) {
    lines.push({ kind: 'CAP', text: <>Shares pool <b>{sharedPool.label}</b> ({formatPoolLimit(sharedPool)} / {sharedPool.period})</> });
  } else if (rule.cap) {
    const k = capKind(rule.cap);
    const v = rule.cap[k];
    const unit = k === 'reward_points' ? ' pts' : ' ₹';
    if (v != null) lines.push({ kind: 'CAP', text: <>Capped at <b>{v.toLocaleString('en-IN')}{unit}</b> per {rule.cap.period || 'monthly'}</> });
  }
  return lines;
}


// ──────────────────────────────────────────────────────────────────────
// Section header used by all steps
// ──────────────────────────────────────────────────────────────────────
const StepHeader = ({ icon: Icon, title, subtitle }) => (
  <div className="space-y-0.5">
    <div className="flex items-center gap-1.5 text-sm font-semibold">
      <Icon className="h-4 w-4 text-accent" />
      {title}
    </div>
    {subtitle && (
      <div className="text-[11px] text-muted-foreground leading-snug">{subtitle}</div>
    )}
  </div>
);
